import { useState, useMemo } from 'react';
import { useProposals } from '@/hooks/useGovernance';
import { useWallet } from '@/hooks/useWallet';
import { useDelegations } from '@/hooks/useStaking';
import { ProposalList } from './ProposalList';
import { VoteModal } from './VoteModal';
import type { ProposalInfo } from '@/types/governance';
import { ProposalStatus } from '@/types/governance';
import { ConnectWalletPrompt } from '@/components/Wallet';
import { text } from '@/config/theme';

export function Governance(): JSX.Element {
  const [selectedProposal, setSelectedProposal] = useState<ProposalInfo | null>(null);
  const { data: proposals, isLoading, refetch } = useProposals();
  const { isConnected, address } = useWallet();
  const { data: delegations, isLoading: delegationsLoading } = useDelegations(address);

  // Calculate total staked GRAV
  const { totalStakedDisplay, hasDelegations } = useMemo(() => {
    if (!delegations?.delegations || delegations.delegations.length === 0) {
      return { totalStakedDisplay: '0', hasDelegations: false };
    }
    
    // Sum up all delegation amounts
    const total = delegations.delegations.reduce((sum, del) => {
      const amount = BigInt(del.balance.amount || '0');
      return sum + amount;
    }, BigInt(0));
    
    // Format for display (GRAV has 6 decimals)
    const totalNum = Number(total) / 1_000_000;
    const formatted = totalNum.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    
    return {
      totalStakedDisplay: formatted,
      hasDelegations: total > BigInt(0),
    };
  }, [delegations]);

  const handleVote = (proposal: ProposalInfo): void => {
    setSelectedProposal(proposal);
  };

  const handleCloseModal = (): void => {
    setSelectedProposal(null);
  };

  const handleVoteSuccess = (): void => {
    // Refetch proposals to get updated tally
    void refetch();
  };

  // Count proposals by status
  const activeCount = proposals?.filter(
    (p) => (p.status as ProposalStatus) === ProposalStatus.VOTING_PERIOD
  ).length ?? 0;
  const passedCount = proposals?.filter(
    (p) => (p.status as ProposalStatus) === ProposalStatus.PASSED
  ).length ?? 0;
  const rejectedCount = proposals?.filter(
    (p) => (p.status as ProposalStatus) === ProposalStatus.REJECTED || (p.status as ProposalStatus) === ProposalStatus.FAILED
  ).length ?? 0;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className={`text-3xl font-bold mb-2 ${text.primary}`}>Governance</h1>
        <p className={`${text.secondary} mb-4`}>
          View and vote on Gravity Bridge governance proposals
        </p>
        {proposals !== undefined && proposals !== null && proposals.length > 0 && (
          <div className={`flex gap-6 text-sm ${text.secondary}`}>
            <div>
              <span className="font-medium">Total:</span> {proposals.length} proposals
            </div>
            {activeCount > 0 && (
              <div className="text-blue-600">
                <span className="font-medium">{activeCount}</span> active voting
              </div>
            )}
            <div>
              <span className="font-medium">{passedCount}</span> passed
            </div>
            <div>
              <span className="font-medium">{rejectedCount}</span> rejected
            </div>
          </div>
        )}
      </div>

      {/* Active Voting Banner */}
      {activeCount > 0 && !isConnected && (
        <div className="mb-6">
          <ConnectWalletPrompt actionDescription="vote on governance proposals" />
        </div>
      )}
      {activeCount > 0 && isConnected && !delegationsLoading && hasDelegations && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div>
              <p className="text-green-800 font-medium">
                Wallet connected! You can vote on {activeCount} active proposal{activeCount !== 1 ? 's' : ''}.
              </p>
              <p className="text-green-700 text-sm">
                Your staked GRAV: <span className="font-semibold">{totalStakedDisplay} GRAV</span>
              </p>
            </div>
          </div>
        </div>
      )}
      {activeCount > 0 && isConnected && !delegationsLoading && !hasDelegations && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <p className="text-yellow-800 font-medium">
              You must delegate tokens to vote on governance proposals.
            </p>
          </div>
        </div>
      )}

      {/* Proposal List */}
      <ProposalList
        proposals={proposals ?? []}
        onVote={handleVote}
        isLoading={isLoading}
      />

      {/* Vote Modal */}
      {selectedProposal && (
        <VoteModal
          proposal={selectedProposal}
          isOpen={true}
          onClose={handleCloseModal}
          onSuccess={handleVoteSuccess}
          hasDelegations={hasDelegations}
          totalStakedDisplay={totalStakedDisplay}
        />
      )}
    </div>
  );
}
